// apps/client/src/pages/conversations/ConversationComposer.tsx
//
// Bottom-of-thread reply textarea + Send button.
//
// Disable Send when ANY of the following hold (canonical plan §1.11 +
// §1.8 belt-and-braces, the backend will refuse the second two too):
//   1. featureFlags.meta_messenger_dm.mode !== 'enforce'
//        → defense-in-depth UI gate; backend `sendOutboundMessage`
//          hard-refuses on 'off' or 'observe' (canonical §1.8 step 2).
//   2. now >= windowExpiresAt (the inbound message + 24h has lapsed)
//        → Meta's standard messaging window has closed; backend will
//          throw failed-precondition / window_expired.
//        Also disable when lastInboundCustomerMessageAt is null (the
//        customer has never sent us a message — no 24h window has ever
//        opened). Canonical plan G14.
//   3. Caller is not the assigned broker AND is not admin/marketing.
//        UI scope hint only; backend `enforceConversationAssignment` is
//        authoritative.
//   4. Text is empty after trim, or exceeds 2000 chars.
//
// Optimistic UI:
//   On Send we generate a `local:<crypto.randomUUID()>` mid and append
//   a pending message via the onOptimistic callback. The parent
//   (ConversationThreadPage) merges optimistic + Firestore-streamed
//   messages and dedupes once the real doc arrives. On HttpsError we
//   call onOptimisticRollback(mid) so the bubble disappears.
import React, { useMemo, useState } from 'react';
import { Send, AlertTriangle, Loader2 } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useSendOutboundMessage } from '../../hooks/useSendOutboundMessage';
import type { MessageDoc } from '../../hooks/useConversationMessages';

const MAX_LEN = 2000;

const ADMIN_LIKE_ROLES = new Set([
  'super_admin',
  'administrator',
  'office_admin',
]);

export interface ConversationComposerProps {
  companyId: string;
  conversationId: string;
  assignedBrokerId: string | null;
  /** = lastInboundCustomerMessageAt + 24h, written by webhook (N1). */
  windowExpiresAt: Timestamp | null;
  /** Source of truth for the 24h window. null → no inbound ever. */
  lastInboundCustomerMessageAt: Timestamp | null;
  /** From company doc; tri-state per canonical §"Strategy". */
  featureFlagMode: 'off' | 'observe' | 'enforce' | undefined;
  /** Current caller's Firebase uid. */
  currentUid: string | undefined;
  /** Current caller's role (from outletContext / userData). */
  currentRole: string | undefined;
  /** Append a pending message to the thread (parent merges). */
  onOptimistic: (msg: MessageDoc) => void;
  /** Drop the pending message after backend rejection. */
  onOptimisticRollback: (mid: string) => void;
}

const ConversationComposer: React.FC<ConversationComposerProps> = ({
  companyId,
  conversationId,
  assignedBrokerId,
  windowExpiresAt,
  lastInboundCustomerMessageAt,
  featureFlagMode,
  currentUid,
  currentRole,
  onOptimistic,
  onOptimisticRollback,
}) => {
  const [text, setText] = useState('');
  const { send, sending, error, errorCode } = useSendOutboundMessage();

  const isMarketing = currentRole === 'marketing';
  const isAdminLike = !!currentRole && ADMIN_LIKE_ROLES.has(currentRole);
  const isAssigned = !!currentUid && assignedBrokerId === currentUid;
  const callerMayReply = isAssigned || isAdminLike || isMarketing;

  const windowExpired = useMemo(() => {
    if (!lastInboundCustomerMessageAt) return true;
    if (!windowExpiresAt) return true;
    return windowExpiresAt.toMillis() <= Date.now();
  }, [windowExpiresAt, lastInboundCustomerMessageAt]);

  const flagAllowsSend = featureFlagMode === 'enforce';

  const trimmed = text.trim();
  const tooLong = text.length > MAX_LEN;
  const isEmpty = trimmed.length === 0;
  const canSend =
    callerMayReply &&
    flagAllowsSend &&
    !windowExpired &&
    !isEmpty &&
    !tooLong &&
    !sending;

  const handleSend = async () => {
    if (!canSend) return;
    // Generate a placeholder mid the optimistic doc can be correlated
    // by. crypto.randomUUID() is widely available; fall back to a
    // timestamp-prefixed random suffix when not (older Safari, jsdom).
    const localId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const localMid = `local:${localId}`;
    const optimistic: MessageDoc = {
      id: localMid,
      mid: localMid,
      direction: 'outbound',
      senderPsid: null,
      senderBrokerId: currentUid ?? null,
      timestamp: Timestamp.now(),
      text: trimmed,
      attachments: [],
      sendStatus: 'pending',
      sendError: null,
      metaMessageId: null,
      receivedAt: Timestamp.now(),
    };
    onOptimistic(optimistic);
    setText('');
    try {
      await send({ companyId, conversationId, text: trimmed });
      // Real doc arrives via Firestore subscription with sendStatus='sent'
      // and the real mid as doc-id; parent's dedupe drops the localMid
      // bubble.
    } catch {
      // Hook already surfaced the error message via state; rollback the
      // optimistic doc so the broker isn't left thinking it went out.
      onOptimisticRollback(localMid);
    }
  };

  // Banner copy — exactly one banner shown at a time, priority order
  // matches the disable cascade above so the broker sees the actual
  // blocker.
  let banner: React.ReactNode = null;
  if (!callerMayReply) {
    banner = (
      <ComposerBanner tone="amber">
        This conversation is assigned to another broker. Only the assigned
        broker (or an admin) can reply.
      </ComposerBanner>
    );
  } else if (featureFlagMode === 'off') {
    banner = (
      <ComposerBanner tone="gray">
        Facebook DM inbox is currently disabled for this workspace. Ask an
        admin to enable Page DM ingestion in feature flags.
      </ComposerBanner>
    );
  } else if (featureFlagMode === 'observe') {
    banner = (
      <ComposerBanner tone="gray">
        Read-only mode — DM ingestion is being verified. Replies are paused
        until an admin flips the feature flag to enforce.
      </ComposerBanner>
    );
  } else if (windowExpired) {
    banner = (
      <ComposerBanner tone="amber">
        24-hour Messenger window expired. Reply via email/SMS instead.
      </ComposerBanner>
    );
  } else if (error) {
    banner = (
      <ComposerBanner tone="red">
        Send failed
        {errorCode && errorCode !== 'unknown' ? ` (${errorCode})` : ''}
        : {error}
      </ComposerBanner>
    );
  }

  return (
    <div className="border-t border-gray-700 bg-gray-900/40 p-3">
      {banner}
      <div className="flex items-end gap-2 mt-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={2}
          maxLength={MAX_LEN}
          disabled={!callerMayReply || !flagAllowsSend || windowExpired}
          placeholder={
            windowExpired
              ? '24-hour window expired'
              : flagAllowsSend
                ? 'Type a reply… (Cmd/Ctrl + Enter to send)'
                : 'Replies disabled'
          }
          className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm resize-none focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          <span>{sending ? 'Sending…' : 'Send'}</span>
        </button>
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
        <span>
          {tooLong
            ? `${text.length} / ${MAX_LEN} — too long`
            : `${text.length} / ${MAX_LEN}`}
        </span>
        {windowExpiresAt && !windowExpired && (
          <span className="text-gray-400">
            Window closes {windowExpiresAt.toDate().toLocaleString('en-AU')}
          </span>
        )}
      </div>
    </div>
  );
};

const ComposerBanner: React.FC<{
  tone: 'amber' | 'red' | 'gray';
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const toneClasses: Record<string, string> = {
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-200',
    red: 'border-red-500/30 bg-red-500/10 text-red-300',
    gray: 'border-gray-600/40 bg-gray-700/20 text-gray-300',
  };
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${toneClasses[tone]}`}
    >
      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
};

export default ConversationComposer;
